// Service for SolutionBuilderAgent to aggregate and register personalization settings via mx.shell's mx-personalization-panel-service
/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.AgentPersonalizationPanelService
 * @module mx.SolutionBuilderAgent
 *
 * @internal
 * Internal service — panel state management only. Use `PersonalizationService` for the public API.
 *
 * @description
 * Registers all existing personalization settings on load.
 */

import { AUTO_SWITCH_TO_NEXT_DASHBOARD_INTERVAL_PRESETS, getAutoSwitchIntervalLabel } from '../../Dashboard/dashboard-personalization.service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { I18nService } from '../../i18n/i18n.service';
import type { PersonalizationService } from './personalization-service';
import type { PersonalizationPanelService, PersonalizationSettingType } from '../../Shell/Pandora/services/mx-personalization-panel-service';
import {
	type PersonalizationItem,
	type PersonalizationDrilldownProperty,
	type PersonalizationDrilldownCard,
	itemToSetting,
} from '../../Shell/Pandora/services/personalization-item';
import { AVAILABLE_ROW_HEIGHTS, DEFAULT_ROW_HEIGHT } from '../../Shell/PandoraSolutionBuilderAgent/layouts/search-page/search-page-helper.service';
import { EventCodes } from '../model/event-codes';
import type { AssistantService, AssistantPanelMode } from '../../Shell/AiAssistant/services/mx-assistant-service';
import type { MxApplicationService } from './mx-application-service';
import type { ThemeService } from '../../Theme/mx-theme.service';
import type { NavigationManager } from '../../Navigation/mx-shell-nav-manager';
import type { EventService } from './event-service';
import type { SidebarService } from '../../Shell/Pandora/services/mx-sidebar-service';
import type { SideLayout } from '../../Shell/Pandora/services/mx-side-layout-service';
import type { ConfigService } from './mx-config-service';
import type { ThemeRecord } from '../../Theme/theme.contracts';
import type { EntityService, EnumerationValue, FragmentDescriptor } from '../../Shell/PandoraSolutionBuilderAgent/services/entity-service';

interface IRoutingService {
	reload(paramsOverride?: Record<string, unknown>): Promise<unknown>;
}

type PersonalizationServiceExt = ReturnType<typeof PersonalizationService> & {
	register(item: PersonalizationItem): void;
	unregister(key: string): void;
	get<T = unknown>(key: string): Promise<T | undefined>;
	set<T = unknown>(key: string, value: T): Promise<void>;
	getRegisteredItems(): Map<string, PersonalizationItem>;
	onChange(key: string, callback: (value: boolean | string | number) => void): () => void;
	clearDefaultNewDialog(storeKey: string): Promise<void>;
	clearDefaultEditDialog(storeKey: string): Promise<void>;
	clearDefaultPreviewDialog(storeKey: string): Promise<void>;
	getAllDefaultNewDialogs(): Promise<Record<string, string>>;
	getAllDefaultEditDialogs(): Promise<Record<string, string>>;
	getAllDefaultPreviewDialogs(): Promise<Record<string, string>>;
	getAllColorContrastPerTheme(): Promise<Record<string, boolean>>;
	clearColorContrastPerTheme(themeId: string): Promise<void>;
	saveGridPersonalization(data: Record<string, unknown>, contextKey: string): Promise<void>;
	saveBoardHorizontalGroupingField(value: unknown, widgetId: string): Promise<void>;
	saveBoardVisibleFields(data: Record<string, unknown>, widgetId: string): Promise<void>;
	saveBoardUseCustomTile(value: boolean, widgetId: string): Promise<void>;
	clearDashboardDescrVisibility(navigationId: string): Promise<void>;
	getAllDashboardDescriptionVisibilities(): Promise<Record<string, boolean>>;
	saveCalendarSettings(contextKey: string, data: Record<string, unknown>): Promise<void>;
	getCalendarSettings(contextKey: string): Promise<Record<string, unknown>>;
	saveSelectedFilters(contextKey: string, filters: Array<{ id: string; favorite: boolean; selected: boolean }>): Promise<void>;
	getAIActionSettings(): Promise<{ RelatedTicketStatus?: number | null; RelatedKnowledgeArticleStatus?: number | null; KnowledgeArticleTemplate?: string | null }>;
	saveAIActionSettings(settings: { RelatedTicketStatus?: number; RelatedKnowledgeArticleStatus?: number; KnowledgeArticleTemplate?: string | null }): Promise<void>;
};

interface IContentWidgetService {
	get(id: string): Promise<{ Name?: string; DataQueryId?: string } | null>;
	getAllViews(forceReload?: boolean, forAllApps?: boolean): Promise<Array<{ Id?: string; Name?: string; DataQueryId?: string }>>;
}
interface INavigationService {
	getNavigationItemFromRoute(id: string, arg: string): Promise<{ title?: string } | null>;
	setCurrentNavigationItem(item: unknown): void;
}
interface IColumnDefinitionService {
	getGridDefinitionById(id: string, widgetId?: string): Promise<{ Name?: string; Columns?: Array<{ Name: string; Title: string }> } | null>;
}
interface INavigationItemsLoaderService {
	loadNavigationItem(id: string): Promise<{ Title?: string; Application?: string } | null>;
}
interface IUserWorkspaceService {
	getUserWorkspaces(): Promise<Array<{ ID: string; Title: string }>>;
	setCurrentUserWorkspace(value: string): Promise<void> | void;
}
interface IRootScope {
	$apply(callback: () => void): void;
}
interface ISearchQueryService {
	clearCache(): Promise<void>;
}

export class AgentPersonalizationPanelService {
	static readonly $inject = [
		'mx.shell.PersonalizationPanelService',
		'mx.internationalization',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.sb.aiAssistant.AssistantService',
		'mx.app.ThemeService',
		'mx.shell.NavigationManager',
		'mx.shell.EventService',
		'mx.shell.SidebarService',
		'mx.shell.SideLayout',
		'mx.shell.Config',
		'mx.shell.InternationalizationService',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.shell.NavigationService',
		'mx.SolutionBuilderAgent.mxColumnDefinitionService',
		'mx.shell.NavigationItemsLoaderService',
		'mx.SolutionBuilderAgent.UserWorkspaceService',
		'mx.shell.AppsService',
		'$rootScope',
		'mx.shell.RoutingService',
		'mx.SolutionBuilderAgent.SearchQueryService',
	];
	constructor(
		private panelService: PersonalizationPanelService,
		private i18n: I18n,
		private personalizationService: PersonalizationServiceExt,
		private assistantService: AssistantService,
		private themeService: ThemeService,
		private navigationManager: NavigationManager,
		private eventService: EventService,
		private sidebarService: SidebarService,
		private sideLayoutService: SideLayout,
		private config: ConfigService,
		private i18nService: I18nService,
		private entityService: EntityService,
		private contentWidgetService: IContentWidgetService,
		private navigationService: INavigationService,
		private columnDefinitionService: IColumnDefinitionService,
		private navigationItemsLoaderService: INavigationItemsLoaderService,
		private userWorkspaceService: IUserWorkspaceService,
		private appsService: MxApplicationService,
		private rootScope: IRootScope,
		private routingService: IRoutingService,
		private searchQueryService: ISearchQueryService
	) {
		this.eventService.subscribeGlobal(EventCodes.PersonalizationItemsChanged, () => this._rebuildPanelSettings());
	}

	private _resolveI18n(key: string): string;
	private _resolveI18n(key: string | undefined): string | undefined;
	private _resolveI18n(key: string | undefined): string | undefined {
		return key ? this.i18n.get(key) ?? key : key;
	}

	private _buildSetting(item: PersonalizationItem, allKeys: Set<string>): PersonalizationSettingType {
		return itemToSetting(
			{
				...item,
				title: this._resolveI18n(item.title),
				category: this._resolveI18n(item.category),
				categoryDescription: this._resolveI18n(item.categoryDescription),
				description: this._resolveI18n(item.description),
				options: item.options?.map((o) => ({ ...o, label: this._resolveI18n(o.label) })),
			},
			(value) => void this.personalizationService.set(item.key, value as boolean | string | number),
			allKeys
		);
	}

	private _rebuildPanelSettings(): void {
		const managedKeys = this.personalizationService.getRegisteredItems();
		const allKeys = new Set(managedKeys.keys());

		const legacy = this.panelService.allSettings.filter((s) => !managedKeys.has(s.id));
		const registered = Array.from(managedKeys.values())
			.filter((item) => !item.hideInPanel)
			.sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
			.map((item) => this._buildSetting(item, allKeys));
		this.panelService.allSettings.length = 0;
		for (const s of [...legacy, ...registered]) {
			this.panelService.allSettings.push(s);
		}
		this.panelService.settingsVersion++;
	}

	/** Default number of cards a drilldown's `childrenLoader` returns per page, before "load more" is needed. */
	private static readonly DRILLDOWN_PAGE_SIZE = 10;

	/**
	 * Slices `entries` to the requested page. `total` is always the full, unsliced length,
	 * so callers can report an accurate count/`hasMore` without resolving every entry.
	 */
	private _paginate<T>(entries: T[], offset = 0, limit = AgentPersonalizationPanelService.DRILLDOWN_PAGE_SIZE): { page: T[]; total: number } {
		return { page: entries.slice(offset, offset + limit), total: entries.length };
	}

	private _extractWidgetId(storageKey: string): string {
		// Complex format: SearchPage::Grid::User_xxx::DataQuery_widgetId
		const dataQueryMatch = storageKey.match(/::DataQuery_(.+)$/);
		if (dataQueryMatch) return dataQueryMatch[1];
		// Simple format: SearchPage_widgetId
		return storageKey.replace(/^SearchPage_/, '');
	}

	private async _getAvailableWidgetInfo(widgetId: string): Promise<{ Name?: string; DataQueryId?: string } | null> {
		// forAllApps: true — personalizations can reference a widget from an app other than
		// whichever one happens to be open right now. Still one bulk, cached call (own cache
		// key from the current-app-only variant), not a per-entry lookup.
		const widgets = await this.contentWidgetService.getAllViews(false, true).catch(() => []);
		return widgets.find((widget) => widget.Id?.toLowerCase() === widgetId.toLowerCase()) ?? null;
	}

	private async _resolveWidgetLabel(widgetId: string): Promise<string> {
		// 1. Nav tree, by route/navigation id (fast, local) — covers ids keyed by the nav item's own id.
		// Deliberately NOT falling back to navigationItemsLoaderService.loadNavigationItem(widgetId)
		// (api/Navigation/Available/{id}) here: it's a raw, unguarded HTTP call with notifications
		// enabled, and this function also receives non-navigation ids (e.g. entity class names from
		// the filters category), so a miss there surfaced as a visible 404 error toast to the user.
		const navItem = await this.navigationService.getNavigationItemFromRoute(widgetId.toLowerCase(), '').catch(() => null);
		if (navItem?.title) return navItem.title;
		// 2. Available widget metadata (bulk-cached, all apps) — covers Designer-created grid/widget ids.
		// Deliberately does NOT fall back to columnDefinitionService.getGridDefinitionById(widgetId): that
		// treats widgetId as a DataQueryId, which it usually isn't — see commit 920a4e6.
		const info = await this._getAvailableWidgetInfo(widgetId);
		if (info?.Name) return info.Name;
		return widgetId;
	}

	private async _resolveGridStorageLabel(storageKey: string): Promise<string> {
		return this._resolveWidgetLabel(this._extractWidgetId(storageKey));
	}

	private async _readGridData(storageKey: string): Promise<Record<string, unknown>> {
		const all = await this.personalizationService.getAllGridPersonalizations();
		return ((all as Record<string, unknown>)[storageKey] as Record<string, unknown>) || {};
	}

	private _stripGridFields(data: Record<string, unknown>, fields: string[]): Record<string, unknown> {
		const result: Record<string, unknown> = {};
		for (const [col, colData] of Object.entries(data)) {
			const cleaned: Record<string, unknown> = { ...(colData as Record<string, unknown>) };
			for (const f of fields) delete cleaned[f];
			if (Object.keys(cleaned).length > 0) result[col] = cleaned;
		}
		return result;
	}

	private async _saveGridData(storageKey: string, data: Record<string, unknown>): Promise<void> {
		const contextKey = this._extractWidgetId(storageKey);
		if (Object.keys(data).length === 0) {
			await this.personalizationService.clearGridPersonalization(storageKey);
			this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey, personalization: {} });
		} else {
			await this.personalizationService.saveGridPersonalization(data, contextKey);
		}
		await this.routingService.reload();
	}

	private async _getColumnTitleMap(storageKey: string): Promise<Map<string, string>> {
		try {
			const widgetId = this._extractWidgetId(storageKey);
			const widgetInfo = await this._getAvailableWidgetInfo(widgetId);
			const def = widgetInfo?.DataQueryId ? await this.columnDefinitionService.getGridDefinitionById(widgetInfo.DataQueryId, widgetId) : null;
			const map = new Map<string, string>();
			if (def?.Columns?.length) {
				for (const col of def.Columns as Array<{ Name: string; Title: string }>) {
					if (col.Name && col.Title) map.set(col.Name, col.Title);
				}
			}
			return map;
		} catch {
			return new Map();
		}
	}

	private _buildGridCardProperties(data: Record<string, unknown>, storageKey: string, titleMap: Map<string, string> = new Map()): PersonalizationDrilldownProperty[] {
		const cols = Object.entries(data) as [string, Record<string, unknown>][];
		const result: PersonalizationDrilldownProperty[] = [];

		const visibilityCols = cols.filter(([, c]) => c.IsDisplay !== undefined || c.visible !== undefined);
		if (visibilityCols.length > 0) {
			const shown = visibilityCols.filter(([, c]) => c.IsDisplay !== false && c.visible !== false).length;
			result.push({
				id: 'columns',
				icon: 'view_column',
				label: this.i18n.get('shell.personalization-panel.columnsShown'),
				type: 'readonly' as const,
				valueSummary: `${shown} of ${visibilityCols.length}`,
				onReset: async () => {
					const current = await this._readGridData(storageKey);
					await this._saveGridData(storageKey, this._stripGridFields(current, ['IsDisplay', 'visible']));
				},
			});
		}

		const positionCols = cols.filter(([, c]) => c.__personalizedPosition !== undefined);
		if (positionCols.length > 0) {
			result.push({
				id: 'order',
				icon: 'reorder',
				label: this.i18n.get('shell.personalization-panel.columnOrder'),
				type: 'readonly' as const,
				valueSummary: this.i18n.get('shell.personalization-panel.rearranged'),
				onReset: async () => {
					const current = await this._readGridData(storageKey);
					await this._saveGridData(storageKey, this._stripGridFields(current, ['__personalizedPosition']));
				},
			});
		}

		const sortingCols = cols.filter(([, c]) => c.Sorting !== undefined).sort((a, b) => ((a[1].SortingPriority as number) ?? 0) - ((b[1].SortingPriority as number) ?? 0));
		if (sortingCols.length > 0) {
			const summary = sortingCols
				.slice(0, 2)
				.map(([name, c]) => `${titleMap.get(name) || name} ${c.Sorting === 'DESC' || c.Sorting === 2 ? '↓' : '↑'}`)
				.join(', then ');
			result.push({
				id: 'sorting',
				icon: 'sort',
				label: this.i18n.get('shell.personalization-panel.sorting'),
				type: 'readonly' as const,
				valueSummary: summary,
				onReset: async () => {
					const current = await this._readGridData(storageKey);
					await this._saveGridData(storageKey, this._stripGridFields(current, ['Sorting', 'SortingPriority']));
				},
			});
		}

		const widthCols = cols.filter(([, c]) => c.Width !== undefined);
		if (widthCols.length > 0) {
			result.push({
				id: 'widths',
				icon: 'aspect_ratio',
				label: this.i18n.get('shell.personalization-panel.widths'),
				type: 'readonly' as const,
				valueSummary: `${widthCols.length} column${widthCols.length !== 1 ? 's' : ''}`,
				onReset: async () => {
					const current = await this._readGridData(storageKey);
					await this._saveGridData(storageKey, this._stripGridFields(current, ['Width']));
				},
			});
		}

		return result;
	}

	private _buildCalendarCardProperties(data: Record<string, unknown>, contextKey: string): PersonalizationDrilldownProperty[] {
		const result: PersonalizationDrilldownProperty[] = [];

		if (data.calendarView) {
			result.push({
				id: 'calendarView',
				icon: 'view_agenda',
				label: this.i18n.get('shell.personalization-panel.calendarView'),
				type: 'readonly' as const,
				valueSummary: String(data.calendarView),
			});
		}

		if (data.showUnassigned !== undefined) {
			result.push({
				id: 'showUnassigned',
				icon: 'person_off',
				label: this.i18n.get('shell.personalization-panel.showUnassigned'),
				type: 'toggle' as const,
				value: !!data.showUnassigned,
				onChange: async (value: boolean | string | number) => {
					await this.personalizationService.saveCalendarSettings(contextKey, { ...data, showUnassigned: !!value });
				},
				onReset: async () => {
					const current = await this.personalizationService.getCalendarSettings(contextKey);
					const rest = { ...current };
					delete rest['showUnassigned'];
					await this.personalizationService.saveCalendarSettings(contextKey, rest);
					await this.personalizationService.flushPendingChanges();
					await this.routingService.reload();
				},
			});
		}

		if (data.groupByExtraDimension) {
			result.push({
				id: 'groupBy',
				icon: 'account_tree',
				label: this.i18n.get('shell.personalization-panel.grouping'),
				type: 'readonly' as const,
				valueSummary: this.i18n.get('shell.personalization-panel.customized'),
			});
		}

		return result;
	}

	/**
	 * @deprecated Settings now self-register via register() in each feature service's initPersonalization().
	 * This method will be emptied once all settings are migrated.
	 */
	public async init() {
		const DEFAULT_TONE = 3;
		const DEFAULT_AUDIENCE = 2;
		const FIND_RELATED_TICKETS_CLOSED_ONLY_STATUS_FILTER = 0;
		const FIND_RELATED_TICKETS_ALL_STATUSES_FILTER = 1;
		const KBA_FINAL_ONLY_STATUS_FILTER = 0;
		const KBA_ALL_STATUSES_FILTER = 1;
		const assistantName = this.config.get('aiAssistantName');

		const contrastMode = await this.themeService.getContrastMode();
		const themeId = await this.themeService.getCurrentThemeId();
		const availableThemes = await this.themeService.getAvailableThemes();
		const navigationPinned = await this.personalizationService.getNavigationPinned();
		const navigationHoverMode = await this.personalizationService.getNavigationHoverMode();
		const navigationCollapsed = await this.personalizationService.getNavigationCollapsed();
		const assistantSettings = await this.personalizationService.getAssistantPanelSettings();
		const rowHeight = await this.personalizationService.getRowHeight();
		const pageSize = await this.personalizationService.getPageSize();
		const gridColumnFiltersPinned = await this.personalizationService.getGridColumnFiltersPinned();
		const filterPanelPinned = await this.personalizationService.getFilterPanelPinned();
		const notificationsStorage = await this.personalizationService.getNotificationsStorage();
		const notificationsPinned = await Promise.resolve(notificationsStorage.getValue('pinned'));
		const sideLayoutPinned = await this.personalizationService.getSideLayoutPinned();
		const dashboardsStorage = await this.personalizationService.getDashboardsStorage();
		const dashboardAutoSwitchInterval = await Promise.resolve(dashboardsStorage.getValue('autoSwitchToNextDashboardInterval'));
		const plainStructureHint = await this.personalizationService.getPlainStructureHintVisibility();
		const newDashboardHint = await this.personalizationService.getTryNewDashboardHintVisibility();
		const filterClickHint = await this.personalizationService.getFilterClickHintVisibility();
		const newReportsHint = await this.personalizationService.getTryNewReportsHintVisibility();
		const filterCtrlKeyHint = await this.personalizationService.getFilterToolbarCtrlKeyHintVisibility();
		const aiSettings = await this.personalizationService.getAISettings();
		const aiActionSettings = await this.personalizationService.getAIActionSettings();

		const [languagesResult, toneResult, audienceResult, kbTemplateResult] = await Promise.allSettled([
			this.i18nService.getLanguages(),
			this.entityService.getEnumerationValues('DWPAIResponseTonePickup'),
			this.entityService.getEnumerationValues('DWPAIAudienceLevelPickup'),
			this.entityService.getFragmentDescriptors({ entityClass: 'ESMKBArticleTemplateClass', displayExpression: 'Subject' }, ''),
		]);

		this.panelService.addPersonalizationSetting({
			id: 'assistant-auto-open',
			category: this.i18n.getFormatted('shell.personalization-panel.category.m42Assist', { AI_assistant_name: assistantName }),
			label: this.i18n.getFormatted('shell.personalization-panel.autoOpenAssistant', { AI_assistant_name: assistantName }),
			description: this.i18n.getFormatted('shell.personalization-panel.autoOpenAssistantHint', { AI_assistant_name: assistantName }),
			type: 'toggle',
			value: (assistantSettings as { autoOpen: boolean }).autoOpen ?? true,
			defaultValue: true,
			onChange: (value: boolean) => {
				this.assistantService.saveAutoOpen(value);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'assistant-display',
			category: this.i18n.getFormatted('shell.personalization-panel.category.m42Assist', { AI_assistant_name: assistantName }),
			label: this.i18n.get('shell.personalization-panel.displayOption'),
			description: this.i18n.getFormatted('shell.personalization-panel.m42AssistDescription', { AI_assistant_name: assistantName }),
			type: 'select',
			value: (assistantSettings as { mode: AssistantPanelMode }).mode,
			defaultValue: 'sidebar',
			options: [
				{
					value: 'sidebar',
					label: this.i18n.get('shell.personalization-panel.sidePanel'),
				},
				{
					value: 'floating',
					label: this.i18n.get('shell.personalization-panel.floatingWindow'),
				},
				{
					value: 'fullscreen',
					label: this.i18n.get('solutionBuilderAgent.layouts.landing-page.enableTvMode'),
				},
			],
			onChange: (value: string) => {
				this.assistantService.setMode(value as AssistantPanelMode);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'theme',
			category: this.i18n.get('shell.personalization-panel.category.theming'),
			label: this.i18n.get('solutionBuilderAgent.common.theme'),
			description: this.i18n.get('shell.personalization-panel.themeDescription'),
			type: 'select',
			value: themeId,
			defaultValue: themeId,
			options: availableThemes.map((t: ThemeRecord) => ({ value: t.id, label: t.title })),
			onChange: async (value: string) => {
				const theme = availableThemes.find((t: ThemeRecord) => t.id === value);
				if (theme) {
					await this.themeService.setUserTheme(theme.id, theme.isContrast);
					const current = await this.themeService.getContrastMode();
					const globalSetting = this.panelService.getPersonalizationSetting('color-contrast');
					if (globalSetting && globalSetting.type === 'toggle') {
						globalSetting.value = !!current;
						this.panelService.settingsVersion++;
					}
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'color-contrast',
			category: this.i18n.get('shell.personalization-panel.category.theming'),
			label: this.i18n.get('shell.personalization-panel.enhancedContrast'),
			description: this.i18n.get('shell.personalization-panel.colorContrastDescription'),
			type: 'toggle',
			value: !!contrastMode,
			defaultValue: false,
			onChange: async (value: boolean) => {
				const current = await this.themeService.getContrastMode();
				if (!!value !== !!current) {
					const newMode = await this.themeService.toggleContrastMode();
					this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
						eventName: `Enhanced Color Contrasts ${newMode ? 'activated' : 'deactivated'}`,
						sender: 'personalization-panel',
					});
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'navigation-pinned',
			category: this.i18n.get('shell.common.navigation'),
			label: this.i18n.get('shell.personalization-panel.pinnedNavigation'),
			description: this.i18n.get('shell.personalization-panel.pinnedNavigationHint'),
			type: 'toggle',
			value: !!navigationPinned,
			defaultValue: false,
			onChange: (value: boolean) => {
				if (value !== this.navigationManager.pinnedMode) {
					if (value && this.navigationManager.collapsedMode) {
						this.navigationManager.setCollapseMode(false, true);
					}
					this.navigationManager.togglePinMode();
				}
				const hoverSetting = this.panelService.getPersonalizationSetting('navigation-hover');
				if (hoverSetting) hoverSetting.disabled = !!value;
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'navigation-hover',
			category: this.i18n.get('shell.common.navigation'),
			label: this.i18n.get('shell.personalization-panel.autoExpandOnHover'),
			description: this.i18n.get('shell.personalization-panel.autoExpandOnHoverHint'),
			type: 'toggle',
			value: !!navigationHoverMode,
			defaultValue: false,
			disabled: !!navigationPinned,
			onChange: (value: boolean) => {
				if (value !== this.navigationManager.hoverMode) {
					this.navigationManager.toggleHoverMode();
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'navigation-collapsed',
			category: this.i18n.get('shell.common.navigation'),
			label: this.i18n.get('shell.personalization-panel.startNavigationCollapsed'),
			description: this.i18n.get('shell.personalization-panel.collapseByDefaultHint'),
			type: 'toggle',
			value: navigationCollapsed ?? true,
			defaultValue: true,
			onChange: (value: boolean) => {
				this.personalizationService.setNavigationCollapsed(value);
				if (this.navigationManager.pinnedMode) {
					this.navigationManager.setCollapseMode(value, true, true);
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'grid-row-height',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('solutionBuilderAgent.components.mx-data-control-settings-panel.density'),
			description: this.i18n.get('shell.personalization-panel.densityHint'),
			type: 'select',
			value: rowHeight ?? DEFAULT_ROW_HEIGHT,
			defaultValue: DEFAULT_ROW_HEIGHT,
			options: AVAILABLE_ROW_HEIGHTS.map((rh) => ({
				value: rh.size,
				label: this.i18n.get(rh.key),
			})),
			onChange: (value: number) => {
				this.personalizationService.setRowHeight(value);
				this.eventService.dispatchEvent(EventCodes.GridRowHeightChanged, value);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'grid-page-size',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('shell.personalization-panel.rowsPerPage'),
			description: this.i18n.get('shell.personalization-panel.rowsPerPageHint'),
			type: 'select',
			value: pageSize ?? (this.config.get('dataSource') as { pageSize: number })?.pageSize,
			defaultValue: (this.config.get('dataSource') as { pageSize: number })?.pageSize,
			options: (this.config.get('dataSource') as { pageSizes: number[] }).pageSizes.map((n) => ({ value: n, label: String(n) })),
			onChange: (value: number) => {
				this.personalizationService.savePageSize(value);
				this.eventService.dispatchEvent(EventCodes.PageSizeChanged, value);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'grid-column-filters-pinned',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('shell.personalization-panel.pinColumnFilters'),
			description: this.i18n.get('shell.personalization-panel.pinnedColumnSearchHint'),
			type: 'toggle',
			value: !!gridColumnFiltersPinned,
			defaultValue: false,
			onChange: (value: boolean) => {
				this.personalizationService.setGridColumnFiltersPinned(value);
				this.eventService.dispatchEvent(EventCodes.GridColumnFiltersPinnedChanged, !!value);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'filter-panel-pinned',
			category: this.i18n.get('shell.common.filters'),
			label: this.i18n.get('shell.personalization-panel.pinFilterPanel'),
			description: this.i18n.get('shell.personalization-panel.pinnedFilterPanelHint'),
			type: 'toggle',
			value: !!filterPanelPinned,
			defaultValue: true,
			onChange: (value: boolean) => {
				this.personalizationService.setFilterPanelPinned(value);
				this.eventService.dispatchEvent(EventCodes.FilterPanelPinnedChanged, !!value);
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'notifications-pinned',
			category: this.i18n.get('shell.personalization-panel.category.pageLayout'),
			label: this.i18n.get('shell.personalization-panel.pinNotificationsPanel'),
			description: this.i18n.get('shell.personalization-panel.pinnedNotificationsPanelHint'),
			type: 'toggle',
			value: !!notificationsPinned,
			defaultValue: false,
			onChange: (value: boolean) => {
				notificationsStorage.setValue('pinned', value);
				if (value !== this.sidebarService.isPinnedOpen) {
					this.sidebarService.togglePinnedOpen();
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'side-layout-pinned',
			category: this.i18n.get('shell.personalization-panel.category.pageLayout'),
			label: this.i18n.get('shell.personalization-panel.pinSidePanel'),
			description: this.i18n.get('shell.personalization-panel.resizeContentForSidePanelsHint'),
			type: 'toggle',
			value: !!sideLayoutPinned,
			defaultValue: false,
			onChange: (value: boolean) => {
				if (value !== this.sideLayoutService.pinned) {
					this.sideLayoutService.togglePin();
				}
			},
		});

		this.panelService.addPersonalizationSetting({
			id: 'dashboard-auto-switch-interval',
			category: this.i18n.get('shell.personalization-panel.category.reporting'),
			label: this.i18n.get('shell.personalization-panel.dashboardAutoSwitchInterval'),
			description: this.i18n.get('shell.personalization-panel.dashboardAutoSwitchIntervalDescription'),
			type: 'select',
			value: dashboardAutoSwitchInterval ?? 60,
			defaultValue: 60,
			options: AUTO_SWITCH_TO_NEXT_DASHBOARD_INTERVAL_PRESETS.map((seconds) => ({
				value: seconds,
				label: getAutoSwitchIntervalLabel(seconds, this.i18n) ?? '',
			})),
			onChange: (value: number) => dashboardsStorage.setValue('autoSwitchToNextDashboardInterval', value),
		});

		this.panelService.addPersonalizationSetting({
			id: 'hint-plain-structure',
			category: this.i18n.get('shell.personalization-panel.guidance'),
			label: this.i18n.get('shell.personalization-panel.hintPlainStructure'),
			description: this.i18n.get('shell.personalization-panel.structureGuidanceHint'),
			type: 'toggle',
			value: !!plainStructureHint,
			defaultValue: true,
			onChange: (value: boolean) => this.personalizationService.setPlainStructureHintVisibility(value),
		});

		this.panelService.addPersonalizationSetting({
			id: 'hint-new-dashboard',
			category: this.i18n.get('shell.personalization-panel.guidance'),
			label: this.i18n.get('shell.personalization-panel.hintNewDashboard'),
			description: this.i18n.get('shell.personalization-panel.newDashboardGuidanceHint'),
			type: 'toggle',
			value: !!newDashboardHint,
			defaultValue: true,
			onChange: (value: boolean) => this.personalizationService.setTryNewDashboardHintVisibility(value),
		});

		this.panelService.addPersonalizationSetting({
			id: 'hint-new-reports',
			category: this.i18n.get('shell.personalization-panel.guidance'),
			label: this.i18n.get('shell.personalization-panel.hintNewReports'),
			description: this.i18n.get('shell.personalization-panel.newReportGuidanceHint'),
			type: 'toggle',
			value: !!newReportsHint,
			defaultValue: true,
			onChange: (value: boolean) => this.personalizationService.setTryNewReportsHintVisibility(value),
		});

		this.panelService.addPersonalizationSetting({
			id: 'hint-filter-click',
			category: this.i18n.get('shell.personalization-panel.guidance'),
			label: this.i18n.get('shell.personalization-panel.hintFilterClick'),
			description: this.i18n.get('shell.personalization-panel.filterSelectionGuidanceHint'),
			type: 'toggle',
			value: !!filterClickHint,
			defaultValue: true,
			onChange: (value: boolean) => this.personalizationService.setFilterClickHintVisibility(value),
		});

		this.panelService.addPersonalizationSetting({
			id: 'hint-filter-ctrl-key',
			category: this.i18n.get('shell.personalization-panel.guidance'),
			label: this.i18n.get('shell.personalization-panel.hintFilterCtrlKey'),
			description: this.i18n.get('shell.personalization-panel.multiSelectFilterGuidanceHint'),
			type: 'toggle',
			value: !!filterCtrlKeyHint,
			defaultValue: true,
			onChange: (value: boolean) => this.personalizationService.setFilterToolbarCtrlKeyHintVisibility(value),
		});

		if (languagesResult.status === 'fulfilled' && languagesResult.value.length) {
			this.panelService.addPersonalizationSetting({
				id: 'ai-language',
				category: this.i18n.get('shell.personalization-panel.category.ai'),
				categoryDescription: this.i18n.get('shell.personalization-panel.category.aiDescription'),
				label: this.i18n.get('shell.personalization-panel.ai.language'),
				description: this.i18n.get('shell.personalization-panel.ai.languageDescription'),
				type: 'select',
				value: aiSettings.Language ?? '',
				defaultValue: '',
				options: [{ value: '', label: this.i18n.get('components.common.none') }, ...languagesResult.value.map((l) => ({ value: l.code, label: l.title }))],
				onChange: (value: string) => this.personalizationService.saveAISettings({ Language: value || null }),
			});
		}

		if (toneResult.status === 'fulfilled' && toneResult.value.length) {
			this.panelService.addPersonalizationSetting({
				id: 'ai-output-tone',
				category: this.i18n.get('shell.personalization-panel.category.ai'),
				label: this.i18n.get('shell.personalization-panel.ai.outputTone'),
				description: this.i18n.get('shell.personalization-panel.ai.outputToneDescription'),
				type: 'select',
				value: aiSettings.OutputTone ?? DEFAULT_TONE,
				defaultValue: DEFAULT_TONE,
				options: toneResult.value.filter((v: EnumerationValue) => !v.Hidden).map((v: EnumerationValue) => ({ value: v.Value, label: v.DisplayString })),
				onChange: (value: number) => this.personalizationService.saveAISettings({ OutputTone: value }),
			});
		}

		if (audienceResult.status === 'fulfilled' && audienceResult.value.length) {
			this.panelService.addPersonalizationSetting({
				id: 'ai-audience-level',
				category: this.i18n.get('shell.personalization-panel.category.ai'),
				label: this.i18n.get('shell.personalization-panel.ai.audienceLevel'),
				description: this.i18n.get('shell.personalization-panel.ai.audienceLevelDescription'),
				type: 'select',
				value: aiSettings.AudienceLevel ?? DEFAULT_AUDIENCE,
				defaultValue: DEFAULT_AUDIENCE,
				options: audienceResult.value.filter((v: EnumerationValue) => !v.Hidden).map((v: EnumerationValue) => ({ value: v.Value, label: v.DisplayString })),
				onChange: (value: number) => this.personalizationService.saveAISettings({ AudienceLevel: value }),
			});
		}

		this.panelService.addPersonalizationSetting({
			id: 'ai-action-related-ticket-status',
			category: this.i18n.get('shell.personalization-panel.category.aiAction'),
			categoryDescription: this.i18n.get('shell.personalization-panel.category.aiActionDescription'),
			label: this.i18n.get('shell.personalization-panel.aiAction.relatedTicketStatus'),
			description: this.i18n.get('shell.personalization-panel.aiAction.relatedTicketStatusDescription'),
			type: 'select',
			value: aiActionSettings.RelatedTicketStatus ?? FIND_RELATED_TICKETS_CLOSED_ONLY_STATUS_FILTER,
			defaultValue: FIND_RELATED_TICKETS_CLOSED_ONLY_STATUS_FILTER,
			options: [
				{ value: FIND_RELATED_TICKETS_CLOSED_ONLY_STATUS_FILTER, label: this.i18n.get('shell.personalization-panel.aiAction.closedTicketsOnly') },
				{ value: FIND_RELATED_TICKETS_ALL_STATUSES_FILTER, label: this.i18n.get('shell.personalization-panel.aiAction.allTickets') },
			],
			onChange: (value: number) => this.personalizationService.saveAIActionSettings({ RelatedTicketStatus: value }),
		});

		this.panelService.addPersonalizationSetting({
			id: 'ai-action-related-knowledge-article-status',
			category: this.i18n.get('shell.personalization-panel.category.aiAction'),
			label: this.i18n.get('shell.personalization-panel.aiAction.relatedKnowledgeArticleStatus'),
			description: this.i18n.get('shell.personalization-panel.aiAction.relatedKnowledgeArticleStatusDescription'),
			type: 'select',
			value: aiActionSettings.RelatedKnowledgeArticleStatus ?? KBA_FINAL_ONLY_STATUS_FILTER,
			defaultValue: KBA_FINAL_ONLY_STATUS_FILTER,
			options: [
				{ value: KBA_FINAL_ONLY_STATUS_FILTER, label: this.i18n.get('shell.personalization-panel.aiAction.finalArticlesOnly') },
				{ value: KBA_ALL_STATUSES_FILTER, label: this.i18n.get('shell.personalization-panel.aiAction.allArticles') },
			],
			onChange: (value: number) => this.personalizationService.saveAIActionSettings({ RelatedKnowledgeArticleStatus: value }),
		});

		if (kbTemplateResult.status === 'fulfilled' && kbTemplateResult.value.length) {
			this.panelService.addPersonalizationSetting({
				id: 'ai-action-knowledge-article-template',
				category: this.i18n.get('shell.personalization-panel.category.aiAction'),
				label: this.i18n.get('shell.personalization-panel.aiAction.knowledgeArticleTemplate'),
				description: this.i18n.get('shell.personalization-panel.aiAction.knowledgeArticleTemplateDescription'),
				type: 'select',
				value: aiActionSettings.KnowledgeArticleTemplate ?? '',
				defaultValue: '',
				options: [
					{ value: '', label: this.i18n.get('components.common.none') },
					...kbTemplateResult.value.map((f: FragmentDescriptor) => ({ value: f.Id, label: f.DisplayString })),
				],
				onChange: (value: string) => this.personalizationService.saveAIActionSettings({ KnowledgeArticleTemplate: value || null }),
			});
		}

		// ── Lists & Grids drilldowns ─────────────────────────────────────────────

		this.panelService.addPersonalizationSetting({
			id: 'personalized-grids',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('shell.personalization-panel.personalizedGrids'),
			description: this.i18n.get('shell.personalization-panel.personalizedGridsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'grid_on',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedGridsDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const [allGrids, allGroupings] = await Promise.all([
					this.personalizationService.getAllGridPersonalizations(),
					this.personalizationService.getAllGroupingPersonalizations(),
				]);
				const hasColumnPersonalization = (d: Record<string, unknown>) =>
					Object.values(d).some(
						(v) =>
							v &&
							typeof v === 'object' &&
							((v as Record<string, unknown>).IsDisplay !== undefined ||
								(v as Record<string, unknown>).visible !== undefined ||
								(v as Record<string, unknown>).Sorting !== undefined ||
								(v as Record<string, unknown>).Width !== undefined ||
								(v as Record<string, unknown>).__personalizedPosition !== undefined)
					);
				const hasGroupingPersonalization = (d: Record<string, unknown>) =>
					!!d && (((d.__groupingBy__ as unknown[] | undefined)?.length ?? 0) > 0 || d.__groupingFilter__ !== undefined);

				const storageKeys = new Set([
					...Object.keys(allGrids).filter((k) => hasColumnPersonalization((allGrids as Record<string, unknown>)[k] as Record<string, unknown>)),
					...Object.keys(allGroupings).filter((k) => hasGroupingPersonalization((allGroupings as Record<string, unknown>)[k] as Record<string, unknown>)),
				]);

				if (limit === 0) {
					return { cards: [], total: storageKeys.size };
				}

				const { page } = this._paginate(Array.from(storageKeys), offset, limit);

				const cards = await Promise.all(
					page.map(async (storageKey) => {
						const gridData = ((allGrids as Record<string, unknown>)[storageKey] as Record<string, unknown>) || {};
						const groupingData = ((allGroupings as Record<string, unknown>)[storageKey] as Record<string, unknown>) || {};
						const [label, titleMap] = await Promise.all([this._resolveGridStorageLabel(storageKey), this._getColumnTitleMap(storageKey)]);
						const properties = this._buildGridCardProperties(gridData, storageKey, titleMap);

						if (hasGroupingPersonalization(groupingData)) {
							const groupingBy = (groupingData.__groupingBy__ as Array<{ Name: string }> | undefined) || [];
							const summary = groupingBy.map((c) => titleMap.get(c.Name) || c.Name).join(', ');
							properties.push({
								id: 'grouping',
								icon: 'account_tree',
								label: this.i18n.get('shell.personalization-panel.grouping'),
								type: 'readonly' as const,
								valueSummary: summary || this.i18n.get('shell.personalization-panel.customized'),
								onReset: async () => {
									await this.personalizationService.clearGroupingPersonalization(storageKey);
								},
							});
						}

						return {
							id: storageKey,
							label,
							onReset: async () => {
								await this._saveGridData(storageKey, {});
								await this.personalizationService.clearGroupingPersonalization(storageKey);
							},
							properties,
						};
					})
				).then((allCards) => allCards.filter((card) => card && card.properties.length > 0));
				return { cards, total: cards.length };
			},
			refreshOn: [EventCodes.GridPersonalizationSaved],
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'grid-personalization',
			category: '',
			parent: 'personalized-grids',
			label: this.i18n.get('shell.personalization-panel.gridPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
		this.panelService.addPersonalizationSetting({
			id: 'grouping-personalization',
			category: '',
			parent: 'personalized-grids',
			label: this.i18n.get('shell.personalization-panel.groupingPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		this.panelService.addPersonalizationSetting({
			id: 'personalized-calendars',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('shell.personalization-panel.personalizedCalendars'),
			description: this.i18n.get('shell.personalization-panel.personalizedCalendarsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'event',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedCalendarsDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const allCalendars = await this.personalizationService.getAllCalendarPersonalizations();
				const calendarEntries = Object.entries(allCalendars).filter(([, data]) => data && typeof data === 'object' && Object.keys(data as object).length > 0);
				const { page, total } = this._paginate(calendarEntries, offset, limit);
				const cards = await Promise.all(
					page.map(async ([contextKey, data]) => ({
						id: contextKey,
						label: await this._resolveWidgetLabel(contextKey),
						onReset: async () => {
							await this.personalizationService.clearCalendarPersonalization(contextKey);
							await this.personalizationService.flushPendingChanges();
							await this.routingService.reload();
						},
						properties: this._buildCalendarCardProperties(data as Record<string, unknown>, contextKey),
					}))
				);
				return { cards, total };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'calendar-personalization',
			category: '',
			parent: 'personalized-calendars',
			label: this.i18n.get('shell.personalization-panel.calendarPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		this.panelService.addPersonalizationSetting({
			id: 'personalized-agile-boards',
			category: this.i18n.get('shell.personalization-panel.category.listsAndGrids'),
			label: this.i18n.get('shell.personalization-panel.personalizedAgileBoards'),
			description: this.i18n.get('shell.personalization-panel.personalizedAgileBoardsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'table_chart',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedAgileBoardsDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const allBoards = await this.personalizationService.getAllGroupingBoardPersonalizations();
				type BoardData = Record<string, unknown>;
				const boardEntries = Object.entries(allBoards).filter(([, data]: [string, unknown]) => {
					const d = data as BoardData;
					return (
						d &&
						typeof d === 'object' &&
						((d.horizontalGroupingField !== null && d.horizontalGroupingField !== undefined) ||
							(d.fields && Object.keys(d.fields as object).length > 0) ||
							(d.visibleFields && Object.keys(d.visibleFields as object).length > 0) ||
							(d.useCustomTile !== null && d.useCustomTile !== undefined))
					);
				});

				if (limit === 0) {
					return { cards: [], total: boardEntries.length };
				}

				const { page } = this._paginate(boardEntries, offset, limit);
				const cards = await Promise.all(
					page.map(async ([storageKey, rawData]: [string, unknown]) => {
						const data = rawData as BoardData;
						const widgetId = this._extractWidgetId(storageKey);
						const properties: PersonalizationDrilldownProperty[] = [];

						if (data.horizontalGroupingField !== null && data.horizontalGroupingField !== undefined) {
							properties.push({
								id: 'horizontalGrouping',
								icon: 'view_column',
								label: this.i18n.get('shell.personalization-panel.boardHorizontalGrouping'),
								type: 'readonly' as const,
								valueSummary: String(data.horizontalGroupingField),
								onReset: async () => {
									await this.personalizationService.saveBoardHorizontalGroupingField(null, widgetId);
									this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey: widgetId, personalization: {} });
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}

						const visibleFieldCount = data.visibleFields ? Object.keys(data.visibleFields as object).length : 0;
						if (visibleFieldCount > 0) {
							properties.push({
								id: 'visibleFields',
								icon: 'view_list',
								label: this.i18n.get('shell.personalization-panel.visibleFields'),
								type: 'readonly' as const,
								valueSummary: String(visibleFieldCount),
								onReset: async () => {
									await this.personalizationService.saveBoardVisibleFields({}, widgetId);
									this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey: widgetId, personalization: {} });
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}

						if (data.useCustomTile !== null && data.useCustomTile !== undefined) {
							properties.push({
								id: 'customTile',
								icon: 'dashboard_customize',
								label: this.i18n.get('shell.personalization-panel.boardCustomTile'),
								type: 'toggle' as const,
								value: !!data.useCustomTile,
								onChange: async (value: boolean | string | number) => {
									await this.personalizationService.saveBoardUseCustomTile(!!value, widgetId);
									this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey: widgetId, personalization: {} });
								},
								onReset: async () => {
									await this.personalizationService.saveBoardUseCustomTile(false, widgetId);
									this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey: widgetId, personalization: {} });
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}

						return {
							id: storageKey,
							label: await this._resolveGridStorageLabel(storageKey),
							onReset: async () => {
								await this.personalizationService.clearGroupingBoardPersonalization(storageKey);
								this.eventService.dispatchEvent(EventCodes.GridPersonalizationSaved, { contextKey: widgetId, personalization: {} });
								await this.personalizationService.flushPendingChanges();
								await this.routingService.reload();
							},
							properties,
						};
					})
				).then((allCards) => allCards.filter((card) => card && card.properties.length > 0));
				return { cards, total: cards.length };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'grouping-board-personalization',
			category: '',
			parent: 'personalized-agile-boards',
			label: this.i18n.get('shell.personalization-panel.groupingBoardPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		// ── Reporting drilldown (BUG fix: dashboard-description + hide-refresh move here) ──

		this.panelService.addPersonalizationSetting({
			id: 'personalized-reports',
			category: this.i18n.get('shell.personalization-panel.category.reporting'),
			label: this.i18n.get('shell.personalization-panel.personalizedReports'),
			description: this.i18n.get('shell.personalization-panel.personalizedReportsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'dashboard',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedReportsDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const [dashboardsStorage, allDescrVisibilities] = await Promise.all([
					this.personalizationService.getDashboardsStorage(),
					this.personalizationService.getAllDashboardDescriptionVisibilities(),
				]);
				const dashboards = (dashboardsStorage.getValue('dashboards') as Record<string, unknown>) || {};
				// Merge dashboard IDs from both sources
				const allIds = new Set([
					...Object.keys(dashboards).filter((id) => {
						const d = dashboards[id] as Record<string, unknown> | null;
						return d && Object.keys(d).length > 0;
					}),
					...Object.keys(allDescrVisibilities || {}).filter((id) => {
						const descrVis = (allDescrVisibilities || {})[id];
						return descrVis === false;
					}),
				]);

				if (limit === 0) {
					return { cards: [], total: allIds.size };
				}

				const { page } = this._paginate(Array.from(allIds), offset, limit);
				const cards = await Promise.all(
					page.map(async (navigationId) => {
						const data = (dashboards[navigationId] as Record<string, unknown>) || {};
						const descrVisible: boolean | undefined = (allDescrVisibilities || {})[navigationId];
						let label = navigationId;
						const navItem = await this.navigationService.getNavigationItemFromRoute(navigationId, '').catch(() => null);
						if (navItem?.title) {
							const appTitle = this.appsService.getCurrentApplication()?.title ?? null;
							label = appTitle ? `${navItem.title} · ${appTitle}` : navItem.title;
						} else {
							// Fallback: direct nav API — returns Title + Application for dashboards
							const item = await this.navigationItemsLoaderService.loadNavigationItem(navigationId).catch(() => null);
							if (item?.Title) {
								const appTitle = item.Application ? this.appsService.getApplications().find((a) => a.value === item.Application)?.title ?? null : null;
								label = appTitle ? `${item.Title} · ${appTitle}` : item.Title;
							}
						}
						const properties: PersonalizationDrilldownProperty[] = [];
						// Show introduction text — per-report toggle (only if explicitly customized)
						if (descrVisible !== undefined) {
							const toggleValue = descrVisible !== false;
							properties.push({
								id: 'description',
								icon: 'article',
								label: this.i18n.get('shell.personalization-panel.showDashboardDescription'),
								type: 'toggle' as const,
								value: toggleValue,
								onChange: async (value: boolean | string | number) => {
									await this.personalizationService.setDashboardDescrVisibility(navigationId, !!value);
									await this.personalizationService.flushPendingChanges();
									this.panelService.settingsVersion++;
									this.eventService.dispatchEvent(EventCodes.DashboardDescrVisibilityChanged, { navigationId, visible: !!value });
								},
								onReset: async () => {
									await this.personalizationService.clearDashboardDescrVisibility(navigationId);
									this.eventService.dispatchEvent(EventCodes.DashboardDescrVisibilityChanged, { navigationId, visible: true });
									await this.personalizationService.flushPendingChanges();
									this.panelService.settingsVersion++;
									await this.routingService.reload();
								},
							});
						}
						// Hide refresh details — per-report toggle stored in dashboards[id].hideRefreshPopover
						if (data['hideRefreshPopover'] !== undefined) {
							properties.push({
								id: 'hideRefresh',
								icon: 'sync_disabled',
								label: this.i18n.get('shell.personalization-panel.dashboardHideRefreshPopover'),
								type: 'toggle' as const,
								value: !!data['hideRefreshPopover'],
								onChange: async (value: boolean | string | number) => {
									const s = await this.personalizationService.getDashboardsStorage();
									const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
									const navData = (all[navigationId] as Record<string, unknown>) || {};
									all[navigationId] = navData;
									navData['hideRefreshPopover'] = !!value;
									await s.setValue('dashboards', all);
								},
								onReset: async () => {
									const s = await this.personalizationService.getDashboardsStorage();
									const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
									const navData = all[navigationId] as Record<string, unknown> | undefined;
									if (navData) {
										delete navData['hideRefreshPopover'];
										if (!Object.keys(navData).length) delete all[navigationId];
									}
									await s.setValue('dashboards', all);
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}
						const widgets = data['widgets'] as Record<string, unknown> | undefined;
						if (widgets && Object.keys(widgets).length > 0) {
							properties.push({
								id: 'widgets',
								icon: 'inventory_2',
								label: this.i18n.get('shell.personalization-panel.widgets'),
								type: 'readonly' as const,
								valueSummary: `${Object.keys(widgets).length}`,
								onReset: async () => {
									const s = await this.personalizationService.getDashboardsStorage();
									const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
									const navData = all[navigationId] as Record<string, unknown> | undefined;
									if (navData) {
										delete navData['widgets'];
										if (!Object.keys(navData).length) delete all[navigationId];
									}
									await s.setValue('dashboards', all);
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}
						if (data['autoRefreshInterval'] !== undefined && data['autoRefreshInterval'] !== null) {
							const interval = data['autoRefreshInterval'] as number;
							properties.push({
								id: 'autoRefresh',
								icon: 'refresh',
								label: this.i18n.get('shell.personalization-panel.autoRefresh'),
								type: 'readonly' as const,
								valueSummary: interval > 0 ? `${interval}s` : (this.i18n.get('shell.common.off') as string),
								onReset: async () => {
									const s = await this.personalizationService.getDashboardsStorage();
									const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
									const navData = all[navigationId] as Record<string, unknown> | undefined;
									if (navData) {
										delete navData['autoRefreshInterval'];
										if (!Object.keys(navData).length) delete all[navigationId];
									}
									await s.setValue('dashboards', all);
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}
						const filterData = data['filters'] as Record<string, unknown> | undefined;
						const filterContent = filterData?.filters as Record<string, unknown> | undefined;
						if (filterContent && Object.keys(filterContent).length > 0) {
							properties.push({
								id: 'filters',
								icon: 'filter_alt',
								label: this.i18n.get('shell.personalization-panel.filters'),
								type: 'readonly' as const,
								valueSummary: this.i18n.get('shell.personalization-panel.customized'),
								onReset: async () => {
									const s = await this.personalizationService.getDashboardsStorage();
									const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
									const navData = all[navigationId] as Record<string, unknown> | undefined;
									if (navData) {
										delete navData['filters'];
										if (!Object.keys(navData).length) delete all[navigationId];
									}
									await s.setValue('dashboards', all);
									await this.personalizationService.flushPendingChanges();
									await this.routingService.reload();
								},
							});
						}
						return {
							id: navigationId,
							label,
							onReset: async () => {
								const s = await this.personalizationService.getDashboardsStorage();
								const all = (s.getValue('dashboards') as Record<string, unknown>) || {};
								delete all[navigationId];
								await s.setValue('dashboards', all);
								await this.personalizationService.clearDashboardDescrVisibility(navigationId);
								await this.personalizationService.flushPendingChanges();
								await this.routingService.reload();
							},
							properties,
						};
					})
				).then((allCards) => allCards.filter((card) => card && card.properties.length > 0));
				return { cards, total: cards.length };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'report-personalization',
			category: '',
			parent: 'personalized-reports',
			label: this.i18n.get('shell.personalization-panel.reportPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		// ── Filters drilldown ────────────────────────────────────────────────────

		this.panelService.addPersonalizationSetting({
			id: 'personalized-filters',
			category: this.i18n.get('shell.common.filters'),
			label: this.i18n.get('shell.personalization-panel.personalizedFilters'),
			description: this.i18n.get('shell.personalization-panel.personalizedFiltersDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'filter_list',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedFiltersDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const allFilters = await this.personalizationService.getAllSelectedFilters();
				type FilterData = { selected?: unknown[]; active?: unknown[] };
				const entries = Object.entries(allFilters).filter(([, data]: [string, unknown]) => {
					const d = data as FilterData;
					return d && (d.selected?.length || d.active?.length);
				});

				// `limit === 0` is a "count only" request from the panel — this category
				// always builds at most 2 aggregate cards (not one per entry), and knowing
				// whether each will exist doesn't need the per-entry label resolution below.
				if (limit === 0) {
					const hasSaved = entries.some(([, data]) => (data as FilterData).selected?.length);
					const hasActive = entries.some(([, data]) => (data as FilterData).active?.length);
					return { cards: [], total: (hasSaved ? 1 : 0) + (hasActive ? 1 : 0) };
				}

				const resolved = await Promise.all(
					entries.map(async ([contextKey, rawData]: [string, unknown]) => {
						const data = rawData as FilterData;
						const label = await this._resolveWidgetLabel(contextKey);
						return { contextKey, label, data };
					})
				);

				const cards: PersonalizationDrilldownCard[] = [];

				const savedProps: PersonalizationDrilldownProperty[] = resolved
					.filter(({ data }) => data.selected?.length)
					.map(({ contextKey, label, data }) => ({
						id: `${contextKey}_saved`,
						icon: 'bookmark',
						label,
						type: 'readonly' as const,
						valueSummary: `${data.selected!.length}`,
						onReset: async () => {
							const activeIds = (data.active ?? []) as string[];
							await this.personalizationService.saveSelectedFilters(
								contextKey,
								activeIds.map((id) => ({ id, favorite: false, selected: true }))
							);
							await this.searchQueryService.clearCache();
							this.eventService.dispatchEvent(EventCodes.PersonalizationSettingChanged, { contextKey });
							await this.personalizationService.flushPendingChanges();
							await this.routingService.reload();
						},
					}));

				const activeProps: PersonalizationDrilldownProperty[] = resolved
					.filter(({ data }) => data.active?.length)
					.map(({ contextKey, label, data }) => ({
						id: `${contextKey}_active`,
						icon: 'filter_alt',
						label,
						type: 'readonly' as const,
						valueSummary: `${data.active!.length}`,
						onReset: async () => {
							const savedIds = (data.selected ?? []) as string[];
							await this.personalizationService.saveSelectedFilters(
								contextKey,
								savedIds.map((id) => ({ id, favorite: true, selected: false }))
							);
							await this.searchQueryService.clearCache();
							this.eventService.dispatchEvent(EventCodes.PersonalizationSettingChanged, { contextKey });
							await this.personalizationService.flushPendingChanges();
							await this.routingService.reload({ filters: [] });
						},
					}));

				if (savedProps.length > 0) {
					cards.push({
						id: 'saved-filters',
						label: this.i18n.get('shell.personalization-panel.savedFilters'),
						onReset: async () => {
							await Promise.all(
								resolved
									.filter(({ data }) => data.selected?.length)
									.map(async ({ contextKey, data }) => {
										const activeIds = (data.active ?? []) as string[];
										await this.personalizationService.saveSelectedFilters(
											contextKey,
											activeIds.map((id) => ({ id, favorite: false, selected: true }))
										);
										await this.searchQueryService.clearCache();
										this.eventService.dispatchEvent(EventCodes.PersonalizationSettingChanged, { contextKey });
									})
							);
							await this.personalizationService.flushPendingChanges();
							await this.routingService.reload();
						},
						properties: savedProps,
					});
				}

				if (activeProps.length > 0) {
					cards.push({
						id: 'active-filters',
						label: this.i18n.get('shell.personalization-panel.activeFilters'),
						onReset: async () => {
							await Promise.all(
								resolved
									.filter(({ data }) => data.active?.length)
									.map(async ({ contextKey, data }) => {
										const savedIds = (data.selected ?? []) as string[];
										await this.personalizationService.saveSelectedFilters(
											contextKey,
											savedIds.map((id) => ({ id, favorite: true, selected: false }))
										);
										await this.searchQueryService.clearCache();
										this.eventService.dispatchEvent(EventCodes.PersonalizationSettingChanged, { contextKey });
									})
							);
							await this.personalizationService.flushPendingChanges();
							await this.routingService.reload({ filters: [] });
						},
						properties: activeProps,
					});
				}

				return { cards, total: cards.length };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'selected-filters-personalization',
			category: '',
			parent: 'personalized-filters',
			label: this.i18n.get('shell.personalization-panel.selectedFiltersPersonalization'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		// ── Navigation drilldown ────────────────────────────

		this.panelService.addPersonalizationSetting({
			id: 'personalized-navigations',
			category: this.i18n.get('shell.common.navigation'),
			label: this.i18n.get('shell.personalization-panel.personalizedNavigations'),
			description: this.i18n.get('shell.personalization-panel.personalizedNavigationsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'menu',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedNavigationsDrilldownDescription'),
			childrenLoader: async (offset?: number, limit?: number) => {
				const favouriteNavigationIds: string[] = (await this.personalizationService.getFavouriteNavigations()) || [];

				if (limit === 0) {
					return { cards: [], total: favouriteNavigationIds.length };
				}

				const { page } = this._paginate(favouriteNavigationIds, offset, limit);
				const cards = await Promise.all(
					page.map(async (navigationId) => {
						let label = navigationId;
						const navItem = await this.navigationService.getNavigationItemFromRoute(navigationId, '').catch(() => null);
						if (navItem?.title) {
							label = navItem.title;
						} else {
							const item = await this.navigationItemsLoaderService.loadNavigationItem(navigationId).catch(() => null);
							if (item?.Title) {
								label = item.Title;
							}
						}
						return {
							id: navigationId,
							label,
							onReset: async () => {
								const current: string[] = (await this.personalizationService.getFavouriteNavigations()) || [];
								await this.personalizationService.saveFavouriteNavigations(current.filter((id) => id !== navigationId));
								await this.personalizationService.flushPendingChanges();
								this.eventService.dispatchEvent(EventCodes.NavigationReload);
								await this.routingService.reload();
							},
							properties: [] as PersonalizationDrilldownProperty[],
						};
					})
				);
				return { cards, total: favouriteNavigationIds.length };
			},
			refreshOn: [EventCodes.PersonalizationSettingChanged],
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'favourite-navigations',
			category: '',
			parent: 'personalized-navigations',
			label: this.i18n.get('shell.personalization-panel.favouriteNavigations'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});

		// ── Dialogs (new category) ───────────────────────────────────────────────

		this.panelService.addPersonalizationSetting({
			id: 'personalized-dialogs',
			category: this.i18n.get('shell.personalization-panel.category.dialogs'),
			label: this.i18n.get('shell.personalization-panel.personalizedDialogs'),
			description: this.i18n.get('shell.personalization-panel.personalizedDialogsDescription'),
			type: 'drilldown',
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'open_in_new',
			drilldownDescription: this.i18n.get('shell.personalization-panel.personalizedDialogsDrilldownDescription'),
			refreshOn: [EventCodes.DefaultDialogChanged],
			childrenLoader: async (offset?: number, limit?: number) => {
				type FavView = { Id: string; DisplayString: string; EntityType: string; EntityDisplayName: string };
				const [allNew, allEdit, allPreview, favouriteViews] = await Promise.all([
					this.personalizationService.getAllDefaultNewDialogs(),
					this.personalizationService.getAllDefaultEditDialogs(),
					this.personalizationService.getAllDefaultPreviewDialogs(),
					this.personalizationService.getFavouriteViews().catch(() => [] as FavView[]),
				]);
				const entityKeys = new Set([...Object.keys(allNew || {}), ...Object.keys(allEdit || {}), ...Object.keys(allPreview || {})]);

				// `limit === 0` is a "count only" request from the panel — this category always
				// builds at most 2 aggregate cards, and whether each exists is known synchronously
				// (no need to resolve dialog names below to answer that).
				if (limit === 0) {
					const hasUpdatedViews = Array.from(entityKeys).some((storeKey) => (allNew || {})[storeKey] || (allEdit || {})[storeKey] || (allPreview || {})[storeKey]);
					const hasSavedViews = (favouriteViews as FavView[]).length > 0;
					return { cards: [], total: (hasUpdatedViews ? 1 : 0) + (hasSavedViews ? 1 : 0) };
				}

				const resolveDialogName = async (id: string): Promise<string> => {
					const info = await this._getAvailableWidgetInfo(id);
					return info?.Name || id;
				};

				const entityPropsPerKey = await Promise.all(
					Array.from(entityKeys).map(async (storeKey) => {
						const entityType = storeKey.replace(/^EntityType_/, '');
						const newId: string | undefined = (allNew || {})[storeKey];
						const editId: string | undefined = (allEdit || {})[storeKey];
						const previewId: string | undefined = (allPreview || {})[storeKey];
						const [newName, editName, previewName] = await Promise.all([
							newId ? resolveDialogName(newId) : Promise.resolve(''),
							editId ? resolveDialogName(editId) : Promise.resolve(''),
							previewId ? resolveDialogName(previewId) : Promise.resolve(''),
						]);
						const props: PersonalizationDrilldownProperty[] = [];
						if (newId) {
							props.push({
								id: `${storeKey}_new`,
								icon: 'add_circle_outline',
								label: entityType,
								type: 'readonly' as const,
								valueSummary: newName,
								onReset: async () => {
									await this.personalizationService.clearDefaultNewDialog(storeKey);
									await this.routingService.reload();
								},
							});
						}
						if (editId) {
							props.push({
								id: `${storeKey}_edit`,
								icon: 'edit',
								label: entityType,
								type: 'readonly' as const,
								valueSummary: editName,
								onReset: async () => {
									await this.personalizationService.clearDefaultEditDialog(storeKey);
									await this.routingService.reload();
								},
							});
						}
						if (previewId) {
							props.push({
								id: `${storeKey}_preview`,
								icon: 'visibility',
								label: entityType,
								type: 'readonly' as const,
								valueSummary: previewName,
								onReset: async () => {
									await this.personalizationService.clearDefaultPreviewDialog(storeKey);
									await this.routingService.reload();
								},
							});
						}
						return props;
					})
				);

				const updatedViewsProperties = entityPropsPerKey.flat();
				const cards: PersonalizationDrilldownCard[] = [];

				if (updatedViewsProperties.length > 0) {
					cards.push({
						id: 'updated-views',
						label: this.i18n.get('shell.personalization-panel.updatedViews'),
						onReset: async () => {
							await Promise.all([
								...Object.keys(allNew || {}).map((k) => this.personalizationService.clearDefaultNewDialog(k)),
								...Object.keys(allEdit || {}).map((k) => this.personalizationService.clearDefaultEditDialog(k)),
								...Object.keys(allPreview || {}).map((k) => this.personalizationService.clearDefaultPreviewDialog(k)),
							]);
							await this.routingService.reload();
						},
						properties: updatedViewsProperties,
					});
				}

				if ((favouriteViews as FavView[]).length > 0) {
					cards.push({
						id: 'saved-views',
						label: this.i18n.get('shell.personalization-panel.savedViews'),
						onReset: async () => {
							await this.personalizationService.saveFavouriteViews([]);
						},
						properties: (favouriteViews as FavView[]).map((view) => ({
							id: view.Id,
							icon: 'star',
							label: view.DisplayString || view.EntityDisplayName || view.Id,
							type: 'readonly' as const,
							valueSummary: '',
							onReset: async () => {
								const current = (await this.personalizationService.getFavouriteViews().catch(() => [])) as FavView[];
								await this.personalizationService.saveFavouriteViews(current.filter((v) => v.Id !== view.Id));
							},
						})),
					});
				}

				return { cards, total: cards.length };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'default-new-dialog',
			category: '',
			parent: 'personalized-dialogs',
			label: this.i18n.get('shell.personalization-panel.defaultNewDialog'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
		this.panelService.addPersonalizationSetting({
			id: 'default-edit-dialog',
			category: '',
			parent: 'personalized-dialogs',
			label: this.i18n.get('shell.personalization-panel.defaultEditDialog'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
		this.panelService.addPersonalizationSetting({
			id: 'default-preview-dialog',
			category: '',
			parent: 'personalized-dialogs',
			label: this.i18n.get('shell.personalization-panel.defaultPreviewDialog'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
		this.panelService.addPersonalizationSetting({
			id: 'favourite-views',
			category: '',
			parent: 'personalized-dialogs',
			label: this.i18n.get('shell.personalization-panel.favouriteViews'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
		const attachmentsViewMode = (await this.personalizationService.getAttachmentsViewMode()) ?? 'list';
		this.panelService.addPersonalizationSetting({
			id: 'attachment-view-mode',
			category: this.i18n.get('shell.personalization-panel.category.dialogs'),
			label: this.i18n.get('shell.personalization-panel.attachmentView'),
			description: this.i18n.get('shell.personalization-panel.attachmentViewDescription'),
			type: 'select',
			value: attachmentsViewMode,
			defaultValue: 'list',
			options: [
				{ value: 'list', label: this.i18n.get('shell.personalization-panel.attachmentViewList') },
				{ value: 'tiles', label: this.i18n.get('shell.personalization-panel.attachmentViewTiles') },
			],
			onChange: async (value: string) => {
				await this.personalizationService.saveAttachmentsViewMode(value);
			},
		});

		// ── Matrix42 Neo: size ─────────────────────────────────────────────────────

		type AssistantLayout = { left?: number; top?: number; width?: number; height?: number };
		const assistantLayout = (assistantSettings as { layout?: AssistantLayout | null })?.layout ?? null;
		const sizeChanged =
			(assistantLayout?.width !== null && assistantLayout?.width !== undefined) || (assistantLayout?.height !== null && assistantLayout?.height !== undefined);
		const positionChanged = (assistantLayout?.left !== null && assistantLayout?.left !== undefined) || (assistantLayout?.top !== null && assistantLayout?.top !== undefined);

		this.panelService.addPersonalizationSetting({
			id: 'assistant-size-changed',
			category: this.i18n.getFormatted('shell.personalization-panel.category.m42Assist', { AI_assistant_name: assistantName }),
			label: this.i18n.get('shell.personalization-panel.assistantSizeChanged'),
			type: 'readonly',
			value: sizeChanged,
			valueSummary: sizeChanged ? (this.i18n.get('shell.common.yes') as string) : (this.i18n.get('shell.common.no') as string),
			defaultValue: false,
			onChange: async (value: boolean) => {
				if (!value) {
					const settings = await this.personalizationService.getAssistantPanelSettings();
					const layout = (settings as { layout?: AssistantLayout | null })?.layout ?? {};
					const rest: Partial<AssistantLayout> = { ...layout };
					delete rest.width;
					delete rest.height;
					await this.personalizationService.saveAssistantPanelSettings({ layout: Object.keys(rest).length ? rest : null });
					const panelEl = document.querySelector<HTMLElement>('mx-assistant-panel');
					if (panelEl) {
						panelEl.style.removeProperty('--panel-w');
						panelEl.style.removeProperty('--panel-h');
					}
				}
			},
		} as unknown as PersonalizationSettingType);

		this.panelService.addPersonalizationSetting({
			id: 'assistant-position-changed',
			category: this.i18n.getFormatted('shell.personalization-panel.category.m42Assist', { AI_assistant_name: assistantName }),
			label: this.i18n.get('shell.personalization-panel.assistantPositionChanged'),
			type: 'readonly',
			value: positionChanged,
			valueSummary: positionChanged ? (this.i18n.get('shell.common.yes') as string) : (this.i18n.get('shell.common.no') as string),
			defaultValue: false,
			onChange: async (value: boolean) => {
				if (!value) {
					const settings = await this.personalizationService.getAssistantPanelSettings();
					const layout = (settings as { layout?: AssistantLayout | null })?.layout ?? {};
					const rest: Partial<AssistantLayout> = { ...layout };
					delete rest.left;
					delete rest.top;
					await this.personalizationService.saveAssistantPanelSettings({ layout: Object.keys(rest).length ? rest : null });
					const panelEl = document.querySelector<HTMLElement>('mx-assistant-panel');
					if (panelEl) {
						panelEl.style.removeProperty('--panel-x');
						panelEl.style.removeProperty('--panel-y');
					}
				}
			},
		} as unknown as PersonalizationSettingType);

		// Keep values in sync while the personalization panel is open
		document.addEventListener('mx:assistant-layout-saved', ((e: CustomEvent) => {
			const layout = e.detail as AssistantLayout;
			const changedLabel = this.i18n.get('shell.common.yes') as string;
			const notChangedLabel = this.i18n.get('shell.common.no') as string;
			const sizeSetting = this.panelService.getPersonalizationSetting('assistant-size-changed');
			const posSetting = this.panelService.getPersonalizationSetting('assistant-position-changed');
			const newSizeChanged = (layout?.width !== null && layout?.width !== undefined) || (layout?.height !== null && layout?.height !== undefined);
			const newPosChanged = (layout?.left !== null && layout?.left !== undefined) || (layout?.top !== null && layout?.top !== undefined);
			this.rootScope.$apply(() => {
				if (sizeSetting && sizeSetting.type === 'readonly') {
					sizeSetting.value = newSizeChanged;
					sizeSetting.valueSummary = newSizeChanged ? changedLabel : notChangedLabel;
				}
				if (posSetting && posSetting.type === 'readonly') {
					posSetting.value = newPosChanged;
					posSetting.valueSummary = newPosChanged ? changedLabel : notChangedLabel;
				}
			});
		}) as EventListener);

		// ── Service Desk (new category) ──────────────────────────────────────────

		const [currentWorkspace, availableWorkspaces] = await Promise.all([
			this.personalizationService.getUserWorkspace(),
			this.userWorkspaceService.getUserWorkspaces().catch(() => []),
		]);
		if (availableWorkspaces?.length) {
			this.panelService.addPersonalizationSetting({
				id: 'default-workspace',
				category: this.i18n.get('shell.personalization-panel.category.serviceDesk'),
				label: this.i18n.get('shell.personalization-panel.defaultWorkspace'),
				type: 'select',
				value: currentWorkspace ?? '',
				defaultValue: '',
				options: [{ value: '', label: this.i18n.get('components.common.none') }, ...availableWorkspaces.map((ws) => ({ value: ws.ID, label: ws.Title }))],
				onChange: async (value: string) => {
					await this.userWorkspaceService.setCurrentUserWorkspace(value);
				},
			});
		}

		// ── Theming: enhanced contrast per theme drilldown ───────────────────────

		this.panelService.addPersonalizationSetting({
			id: 'contrast-per-theme',
			category: this.i18n.get('shell.personalization-panel.category.theming'),
			label: this.i18n.get('shell.personalization-panel.contrastPerTheme'),
			description: this.i18n.get('shell.personalization-panel.contrastPerThemeDescription'),
			type: 'drilldown',
			refreshOn: [EventCodes.ThemeChanged],
			value: null,
			defaultValue: null,
			onChange: () => {},
			icon: 'tonality',
			drilldownDescription: this.i18n.get('shell.personalization-panel.contrastPerThemeDrilldownDescription'),
			childrenLoader: async () => {
				const [allContrast, availableThemes] = await Promise.all([this.personalizationService.getAllColorContrastPerTheme(), this.themeService.getAvailableThemes()]);
				const cards = (availableThemes as ThemeRecord[]).map((theme) => ({
					id: theme.id,
					label: theme.title,
					type: 'toggle' as const,
					value: !!allContrast[theme.id],
					defaultValue: false,
					onChange: async (value: boolean) => {
						await this.personalizationService.setEnhancedColorContrastMode(theme.id, value);
						const currentThemeId = await this.themeService.getCurrentThemeId();
						if (theme.id === currentThemeId) {
							const current = await this.themeService.getContrastMode();
							if (value !== !!current) await this.themeService.toggleContrastMode();
							const globalSetting = this.panelService.getPersonalizationSetting('color-contrast');
							if (globalSetting && globalSetting.type === 'toggle') {
								globalSetting.value = value;
								this.panelService.settingsVersion++;
							}
						}
					},
					onReset: async () => {
						await this.personalizationService.clearColorContrastPerTheme(theme.id);
						const currentThemeId = await this.themeService.getCurrentThemeId();
						if (theme.id === currentThemeId) {
							const current = await this.themeService.getContrastMode();
							if (current) await this.themeService.toggleContrastMode();
							// Sync global toggle back to false
							const globalSetting = this.panelService.getPersonalizationSetting('color-contrast');
							if (globalSetting && globalSetting.type === 'toggle') {
								globalSetting.value = false;
								this.panelService.settingsVersion++;
							}
						}
					},
					properties: [],
				}));
				return { cards, total: cards.length };
			},
		} as unknown as PersonalizationSettingType);
		this.panelService.addPersonalizationSetting({
			id: 'color-contrast-per-theme',
			category: '',
			parent: 'contrast-per-theme',
			label: this.i18n.get('shell.personalization-panel.colorContrastPerTheme'),
			type: 'array',
			value: null,
			defaultValue: null,
			onChange: () => {},
		});
	}
}
